import http from 'axios';
import L    from 'leaflet';

class M5GISUtils {

    /**
     * Constants
     */

    // GeoServer server
    GeoserverUrl = 'https://www.m5gis.com';  // 'http://test.m5gis.com:8090'

    // proxy server references
    Proxy = './proxy';

    // potential properties, error code for feature intersection query
    PropertyGeom = 'geom';
    PropertyTheGeom = 'the_geom';
    PropertyInvalidCode = 'InvalidParameterValue';

    // GeoServer process call base for buffer requests
    BaseAbutterUrl = '/geoserver/wps?service=WPS&version=1.0.0&request=Execute&identifier=py:buffer&';

    // return jsons
    AbutterJSON = '{"status":"","coordinates":"","ids":""}';
    AbutterRingJSON = '{"status":"","coordinates":""}';
    AbutterFeaturesJSON = '{"status":"","ids":""}';

    getProxyUrl(processUrl) {
        return `./proxy?${ processUrl }`
    }

    getProxyUrlGeoServer(geoServerUrl) {
        return `./proxy?${ geoServerUrl }/geoserver/wms`;
    }

    getFeaturesByIntersect(layer, property, gml) {
        return '<wfs:GetFeature xmlns:wfs="http://www.opengis.net/wfs" outputFormat="json" service="WFS" version="1.1.0" maxFeatures="10000" xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.1.0/wfs.xsd" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:gml="http://www.opengis.net/gml">' +
            `<wfs:Query typeName="${ layer }" srsName="EPSG:4326">` +
            '<ogc:Filter xmlns:ogc="http://www.opengis.net/ogc">' +
            '<ogc:Intersects>' +
            `<ogc:PropertyName>${ property }</ogc:PropertyName>` +
            `<gml:Polygon srsName="EPSG:4326"><gml:exterior><gml:LinearRing srsName="EPSG:4326"><gml:coordinates>${ gml }</gml:coordinates></gml:LinearRing></gml:exterior></gml:Polygon>` +
            '</ogc:Intersects>' +
            '</ogc:Filter>' +
            '</wfs:Query>' +
            '</wfs:GetFeature>';
    }

    getAbutterBuffer(buffer, wkt, srid, distance) {
        return this.BaseAbutterUrl + `datainputs=geom=${ buffer }(${ wkt })@mimetype=application/wkt;srid=${ srid };distance=${ distance }`;
    }

    /**
     * Main Functions
     */

    /**
     * Returns abutter ring coordinates for a provided layer
     *
     * @param layer the feature layer reference/url to use for the feature request
     * @param geometry the geometry containing coordinates to be abutted
     * @param distance the abutter ring distance
     * @param srid the spatial reference identifier of the layer/map
     * @param id the abutter primary identifier feature field
     * @param token token (esp. for ArcGIS) if required, null if not
     * @param callback returns status (empty on success), ring coordinates and ids as json
     */
    getAbutter(layer, geometry, distance, srid, id, token, callback) {
        var me = this;
        var json = JSON.parse(me.AbutterJSON);

        // evaluate first
        if (layer && geometry && (distance != 'null') && srid && callback) {
            // get the abutter ring first
            me.getAbutterRing(geometry, distance, srid, function (jsonRing) {

                // parse, then continue or return error
                if (!jsonRing.status) {
                    // next, get abutter ids
                    me.getAbutterIds(jsonRing.coordinates, layer, id, token, function (jsonIds) {

                        // parse, then continue or return error
                        if (!jsonIds.status) {
                            json.coordinates = jsonRing.coordinates;
                            json.ids = jsonIds.ids;
                            callback(json);
                        } else {
                            me.callbackStatus(callback, json, jsonIds.status);
                        }
                    });
                } else {
                    me.callbackStatus(callback, json, jsonRing.status);
                }
            });
        } else {
            me.callbackStatus(callback, json, 'Problem with abutter request.');
        }
    }

    /**
     * Returns abutter ring coordinates for a provided layer
     *
     * @param geometry the geometry containing coordinates to be abutted
     * @param distance the abutter ring distance
     * @param srid the spatial reference identifier of the layer/map
     * @param callback returns status (empty on success) and coordinates as json
     */
    getAbutterRing(geometry, distance, srid, callback) {
        const me = this;
        const json = JSON.parse(me.AbutterRingJSON);

        // evaluate first
        if (geometry && (distance != 'null') && srid && callback) {
            // prepare query based on geometry type
            let wkt, processQuery;

            // assemble request
            if (geometry.type === 'Point') {
                wkt = me.coordinatesToWkt(geometry.coordinates);
                processQuery = me.getAbutterBuffer('POINT', wkt, srid, distance);
            } else if (geometry.type === 'LineString') {
                wkt = me.coordinatesToWkt(geometry.coordinates);
                processQuery = me.getAbutterBuffer('LINESTRING', wkt, srid, distance);
            } else if ((geometry.type === 'Polygon') || (geometry.type === 'MultiPolygon')) {
                wkt = me.coordinatesToWkt(geometry.coordinates[0]);
                processQuery = me.getAbutterBuffer('POLYGON', '(' + wkt + ')', srid, distance);
            }
            const processUrl = me.GeoserverUrl + processQuery;

            http.get(`${ me.getProxyUrl(processUrl) }`)
                .then(response => {
                    if (response.data.includes('<gml:posList>')) {
                        // extract, convert exterior (ingore interior) gml results to coordinates
                        const exterior = response.data.match(new RegExp('<gml:exterior>' + "(.*)" + '</gml:exterior>'))[1];
                        const gml = exterior.match(new RegExp('<gml:posList>' + "(.*)" + '</gml:posList>'))[1];
                        json.coordinates = me.gmlToCoordinates(gml);
                        callback(json);
                    } else {
                        me.callbackStatus(callback, json, 'Problem with abutting parcel (multiple polygons?)');
                    }
                }).catch(err => {
                me.callbackStatus(callback, json, 'Failure getting abutter buffer ring.');
            });
        } else {
            me.callbackStatus(callback, json, 'Problem with abutter ring request.');
        }
    }

    /**
     * Returns abutter ids for a provided layer intersecting and contained within coordinates.
     *
     * @param coordinates coordinates of buffer ring for feature intersections
     * @param layer the feature layer to use for the feature request
     * @param id the abutter primary identifier feature field
     * @param token token (esp. for ArcGIS) if required, null if not
     * @param callback returns status (empty on success) and ids as json
     */
    getAbutterIds(coordinates, layer, id, token, callback) {
        var me = this;
        var json = JSON.parse(me.AbutterFeaturesJSON);

        // evaluate first
        if (coordinates && layer && callback) {
            // route layer: AppGeo, ArcGIS or Geoserver (default)
            if (me.isAppGeoFeatureService(layer)) {
                // call AppGeo spatial query using /search prefix
                var config = {
                    headers: {'Content-Type': 'application/json'}
                };
                http.post(layer + '/search', me.coordinatesToFeatureGeometry(coordinates), config)
                    .then(response => {
                        if (response.data) {
                            json.ids = me.featuresToIds(response.data.features, id);
                            callback(json);
                        } else {
                            me.callbackStatus(callback, json, 'Problem with abutter request format.');
                        }
                    })
                    .catch(err => me.callbackStatus(callback, json, 'Problem with AppGeo abutter feature request.'))
            } else if (me.isArcGisFeatureService(layer)) {
                try {
                    // get all of the parcels that intersect the buffer; issue: no error handling
                    var query = L.esri.query({
                        url: layer
                    });
                    if (token) {
                        query.token(token);
                    }
                    query.intersects(me.coordinatesToGeometry(coordinates));
                    query.run(function (error, featureCollection, response) {
                        json.ids = me.featuresToIds(featureCollection.features, id);
                        callback(json);
                    });
                } catch (e) {
                    me.callbackStatus(callback, json, 'Problem with ArcGIS abutter request.');	            
                }
            } else {
                // convert geometry array to gml coordinates list for XML
                var gml = me.coordinatesToGml(coordinates);

                // try the_geom property first, geom on failure
                me.geoserverFeatureIntersect(gml, layer, me.PropertyTheGeom, id, function (json) {
                    if (json.status && json.status.includes(me.PropertyInvalidCode)) {
                        me.geoserverFeatureIntersect(gml, layer, me.PropertyGeom, id, function (json) {
                            callback(json);
                        });
                    } else {
                        callback(json);
                    }
                });
            }
        } else {
            me.callbackStatus(callback, json, 'Problem with abutter feature request.');
        }
    }

    /**
     * Processing Utilities
     */

    /**
     * Makes an intersect query to GeoServer using a provided geometry property.
     *
     * @param gml the coordinates in GML format
     * @param layer the feature layer to use for the feature request
     * @param property the geometry property
     * @param id the abutter primary identifier feature field
     * @param callback returns status (empty on success) and features as json
     */
    geoserverFeatureIntersect(gml, layer, property, id, callback) {
        var me = this;
        var json = JSON.parse(me.AbutterFeaturesJSON);

        const xmlData = this.getFeaturesByIntersect(layer, property, gml);

        var config = {
            headers: {'Content-Type': 'text/xml'}
        };

        http.post(this.getProxyUrlGeoServer(this.GeoserverUrl), xmlData, config)
            .then(response => {
                try {
                    if (typeof response.data === 'string') {
                        me.callbackStatus(callback, json, response.data);
                    } else {
                        json.ids = me.featuresToIds(response.data.features, id);
                        callback(json);	
                    }
                } catch (e) {
                    me.callbackStatus(callback, json, response.data);
                }
            }).catch(err => me.callbackStatus(callback, json, 'Failed to collect abutter features.'))
    }

    /**
     * Extracts the primary identifiers from the list of features.  Note: no error handling.
     *
     * @param features the list of features
     * @param id the primary feature identifier to extract
     * @return array of feature ids
     */
    featuresToIds(features, id) {
        var ids: any[] = [];

        try {
            for (var i = 0; i < features.length; i++) {
                if (features[i].properties[id]) {
                    ids.push(features[i].properties[id]);
                }
            }
        } catch (e) {
        }

        return ids;
    }

    /**
     * Coordinate Utilities
     */

    // convert gml coordinates "x1 y1 x2 y2 ...xn yn" tp coordinate array [[x1,y1],[x2,y2],...[xn,yn]]
    gmlToCoordinates(s) {
        var a: any[] = [];
        var b: any[] = [];
        var list = s.split(' ');
        for (var i = 0; i < list.length; i += 2) {
            try {
                a.push([parseFloat(list[i]), parseFloat(list[i + 1])]);
            } catch (err) {
                a.push([list[i], list[i + 1]]);
            }
        }
        return a;
    }

    // convert coordinate array [[x1,y1],[x2,y2],...[xn,yn]] to gml coordinates "x1,y1 x2,y2 ...xn,yn"
    coordinatesToGml(a) {
        var s = JSON.stringify(a);
        s = s.replace(/\],\[/g, ' ');
        s = s.replace(/\[/g, '');
        s = s.replace(/\]/g, '');
        return s;
    }

    // convert coordinates array [[x1,y1],[x2,y2],...[xn,yn]] to wkt coordinates "x1 y1,x2 y2, ...xn yn"
    coordinatesToWkt(a) {
        var s = JSON.stringify(a);
        s = s.replace(/,/g, ' ');
        s = s.replace(/\] \[/g, ',');
        s = s.replace(/\[/g, '');
        s = s.replace(/\]/g, '');
        return s;
    }

    // convert a coordinate array into a geometry object
    coordinatesToGeometry(coordinates) {
        var s = JSON.stringify(coordinates);
        var sGeometry = `{"type":"Polygon","coordinates":[${ s }]}`;
        return JSON.parse(sGeometry);
    }

    // convert a coordinate array into feature geometry
    coordinatesToFeatureGeometry(coordinates) {
        var s = "[[" + coordinates.join("],[") + "]]"
        var sGeometry = `{"geometry":{"type":"Feature","geometry":{"type":"Polygon","coordinates":[${ s }]}},"returnAll":true}`;
        return sGeometry;
    }

    /**
     * Utilities
     */

    isAppGeoFeatureService(layer) {
        return ((layer.indexOf("appgeo") >= 0) || (layer.indexOf("mapgeo") >= 0));
    }

    isArcGisFeatureService(layer) {
        return layer && (layer.startsWith('http') || layer.startsWith(this.Proxy + '?http')) && !this.isAppGeoFeatureService(layer);
    }

    callbackStatus(callback, json, status) {
        json.status = status;
        callback(json);
    }
}

export const util = new M5GISUtils();
